/**
 * Disposition justifiée (« Google Photos ») : on remplit des rangées de largeur
 * fixe (le conteneur) en préservant le ratio de chaque asset, puis on met la
 * rangée à l'échelle pour qu'elle occupe exactement la largeur disponible.
 *
 * Purement géométrique et déterministe → testable sans DOM. La virtualisation
 * (montage des seules rangées visibles) est gérée par-dessus dans le composant.
 *
 * GÉNÉRIQUE sur l'asset (`T extends Sized`) : ce module ne lit QUE `width`/`height`.
 * Le typer sur `GalleryAsset` obligeait la grille des PARTAGES PUBLICS (XPHOTO-31) —
 * dont le DTO ne porte volontairement ni chemin ni nom de fichier — à fabriquer un
 * faux `GalleryAsset`, c'est-à-dire à inventer exactement les champs qu'on refuse de
 * faire sortir. La contrainte structurelle dit la vérité : la géométrie se moque du
 * reste.
 */

/** Le strict nécessaire à la disposition : des dimensions, éventuellement inconnues. */
export interface Sized {
  width: number | null;
  height: number | null;
}

/** Ratio de repli quand l'asset n'a pas de dimensions connues (carré). */
const FALLBACK_RATIO = 1;

/** Une tuile placée : l'asset + sa boîte finale (px), ratio réservé (anti-CLS). */
export interface LayoutTile<T extends Sized = Sized> {
  asset: T;
  width: number;
  height: number;
}

/** Une rangée justifiée : hauteur commune + tuiles à largeurs variables. */
export interface LayoutRow<T extends Sized = Sized> {
  /** Index absolu de la rangée (stable, pour la clé de virtualisation). */
  index: number;
  /**
   * Index absolu du PREMIER asset de la rangée (somme des tuiles des rangées
   * précédentes). Calculé UNE FOIS ici (audit B3) : la grille en avait besoin pour chaque
   * rangée rendue et le retrouvait par une recherche linéaire par identité d'objet —
   * ~10 rangées visibles × 6 000 rangées ≈ 60 000 itérations par rendu, pour une donnée
   * que la disposition connaît déjà en la construisant.
   */
  startIndex: number;
  height: number;
  tiles: LayoutTile<T>[];
}

export interface JustifiedLayout<T extends Sized = Sized> {
  rows: LayoutRow<T>[];
  /** Hauteur totale du contenu (somme des rangées + gouttières), pour le scroller. */
  totalHeight: number;
}

/** Ratio largeur/hauteur d'un asset, borné pour éviter les tuiles dégénérées. */
function aspectRatio(asset: Sized): number {
  if (!asset.width || !asset.height) return FALLBACK_RATIO;
  const ratio = asset.width / asset.height;
  if (!Number.isFinite(ratio) || ratio <= 0) return FALLBACK_RATIO;
  // Panoramiques / tranches extrêmes : on borne pour garder des rangées lisibles.
  return Math.min(Math.max(ratio, 0.25), 4);
}

/**
 * Calcule les rangées justifiées.
 *
 * @param assets           assets à disposer (dans l'ordre)
 * @param containerWidth   largeur utile du conteneur (px)
 * @param targetRowHeight  hauteur de rangée visée (px) — vient de la densité
 * @param gap              gouttière entre tuiles ET entre rangées (px)
 */
export function computeJustifiedLayout<T extends Sized>(
  assets: readonly T[],
  containerWidth: number,
  targetRowHeight: number,
  gap: number,
): JustifiedLayout<T> {
  const rows: LayoutRow<T>[] = [];
  if (containerWidth <= 0 || assets.length === 0) {
    return { rows, totalHeight: 0 };
  }

  let current: T[] = [];
  let currentRatioSum = 0;
  let rowIndex = 0;
  // Index absolu du premier asset de la rangée en cours de constitution.
  let rowStartIndex = 0;

  const flush = (isLast: boolean): void => {
    if (current.length === 0) return;
    const gapsWidth = gap * (current.length - 1);
    const available = containerWidth - gapsWidth;
    // Hauteur qui fait tenir la rangée pile dans la largeur dispo. Pour la
    // dernière rangée incomplète, on ne dépasse pas la hauteur cible (pas
    // d'étirement disgracieux d'une rangée à moitié pleine).
    let height = available / currentRatioSum;
    if (isLast) height = Math.min(height, targetRowHeight);

    const tiles: LayoutTile<T>[] = current.map((asset) => ({
      asset,
      width: aspectRatio(asset) * height,
      height,
    }));
    rows.push({ index: rowIndex, startIndex: rowStartIndex, height, tiles });
    rowIndex += 1;
    rowStartIndex += tiles.length;
    current = [];
    currentRatioSum = 0;
  };

  for (const asset of assets) {
    current.push(asset);
    currentRatioSum += aspectRatio(asset);
    // La rangée est « pleine » quand, à la hauteur cible, sa largeur (ratios +
    // gouttières) atteint la largeur du conteneur.
    const projectedWidth = currentRatioSum * targetRowHeight + gap * (current.length - 1);
    if (projectedWidth >= containerWidth) {
      flush(false);
    }
  }
  flush(true);

  const totalHeight =
    rows.reduce((sum, r) => sum + r.height, 0) + Math.max(0, rows.length - 1) * gap;

  return { rows, totalHeight };
}
